/**
 * Tableau de conversion — logique pure (Boîte à outils, 6e → 2nde).
 *
 * Longueurs, masses, contenances (un chiffre par unité), aires (deux),
 * volumes (trois, avec les litres sous dm³ et cm³), vitesses (km/h ↔ m/s).
 * Les nombres restent EXACTS : chaîne de chiffres (BigInt) et position de la
 * virgule ; convertir, c'est déplacer la virgule de k rangs par unité.
 */

export type Kind = 'length' | 'mass' | 'capacity' | 'area' | 'volume';

export interface UnitDef {
  id: string;
  /** « cm », « m² », « dm³ » */
  label: string;
  /** « centimètre » */
  name: string;
  /** exposant de 10 du chiffre des unités de cette unité, dans l'unité de base */
  pos: number;
  /** autre nom posé sous une colonne du tableau (L sous dm³, ha sous hm²…) */
  alias?: boolean;
}

export interface KindDef {
  id: Kind;
  title: string;
  /** chiffres par unité dans le tableau */
  digits: 1 | 2 | 3;
  units: readonly UnitDef[];
  minRank: number;
}

const prefixes = [
  ['k', 'kilo', 3],
  ['h', 'hecto', 2],
  ['da', 'déca', 1],
  ['', '', 0],
  ['d', 'déci', -1],
  ['c', 'centi', -2],
  ['m', 'milli', -3],
] as const;

const simple = (sym: string, name: string): UnitDef[] =>
  prefixes.map(([p, n, e]) => ({
    id: `${p}${sym}`,
    label: `${p}${sym}`,
    name: `${n}${name}`,
    pos: e,
  }));

export const KINDS: readonly KindDef[] = [
  { id: 'length', title: 'Longueurs', digits: 1, units: simple('m', 'mètre'), minRank: 1 },
  {
    id: 'mass',
    title: 'Masses',
    digits: 1,
    units: [
      { id: 't', label: 't', name: 'tonne', pos: 6 },
      { id: 'q', label: 'q', name: 'quintal', pos: 5 },
      ...simple('g', 'gramme'),
    ],
    minRank: 1,
  },
  {
    id: 'capacity',
    title: 'Contenances',
    digits: 1,
    // le kilolitre ne sert pas : le tableau commence à l'hectolitre
    units: simple('L', 'litre').filter((u) => u.id !== 'kL'),
    minRank: 1,
  },
  {
    id: 'area',
    title: 'Aires',
    digits: 2,
    units: [
      ...prefixes.map(([p, n, e]) => ({
        id: `${p}m2`,
        label: `${p}m²`,
        name: `${n}mètre carré`,
        pos: 2 * e,
      })),
      // unités agraires, sous hm², dam² et m²
      { id: 'ha', label: 'ha', name: 'hectare', pos: 4, alias: true },
      { id: 'a', label: 'a', name: 'are', pos: 2, alias: true },
      { id: 'ca', label: 'ca', name: 'centiare', pos: 0, alias: true },
    ],
    minRank: 1,
  },
  {
    id: 'volume',
    title: 'Volumes',
    digits: 3,
    units: [
      { id: 'm3', label: 'm³', name: 'mètre cube', pos: 0 },
      { id: 'dm3', label: 'dm³', name: 'décimètre cube', pos: -3 },
      { id: 'cm3', label: 'cm³', name: 'centimètre cube', pos: -6 },
      { id: 'mm3', label: 'mm³', name: 'millimètre cube', pos: -9 },
      { id: 'hL', label: 'hL', name: 'hectolitre', pos: -1, alias: true },
      { id: 'daL', label: 'daL', name: 'décalitre', pos: -2, alias: true },
      { id: 'L', label: 'L', name: 'litre', pos: -3, alias: true },
      { id: 'dL', label: 'dL', name: 'décilitre', pos: -4, alias: true },
      { id: 'cL', label: 'cL', name: 'centilitre', pos: -5, alias: true },
      { id: 'mL', label: 'mL', name: 'millilitre', pos: -6, alias: true },
    ],
    minRank: 2,
  },
];

export const kindOf = (id: Kind) => KINDS.find((k) => k.id === id)!;

/* ------------------------------------------------------------------ */
/* nombres décimaux exacts                                             */
/* ------------------------------------------------------------------ */

/** valeur = mantisse × 10^exp (mantisse entière ≥ 0) */
export interface Dec {
  m: bigint;
  exp: number;
}

/** « 3,25 », « 0.5 », « 1 200 » (15 chiffres avant la virgule, 12 après) ; null sinon */
export function parseDec(text: string): Dec | null {
  const t = text.trim().replace(/\s/g, '').replace(',', '.');
  const r = /^(\d{1,15})(?:\.(\d{1,12}))?$/.exec(t);
  if (!r) return null;
  const frac = r[2] ?? '';
  return normalize({ m: BigInt(r[1]! + frac), exp: -frac.length });
}

/** sans zéro inutile à droite de la mantisse */
function normalize(d: Dec): Dec {
  let { m, exp } = d;
  if (m === 0n) return { m: 0n, exp: 0 };
  while (m % 10n === 0n) {
    m /= 10n;
    exp += 1;
  }
  return { m, exp };
}

export const shift = (d: Dec, k: number): Dec => normalize({ m: d.m, exp: d.exp + k });

const NNBSP = String.fromCharCode(0x202f);

/** écriture française : « 3,25 », « 12 500 », « 0,004 » */
export function decText(d: Dec, sep = NNBSP): string {
  if (d.m === 0n) return '0';
  const digits = d.m.toString();
  let int: string;
  let frac = '';
  if (d.exp >= 0) int = digits + '0'.repeat(d.exp);
  else if (-d.exp >= digits.length) {
    int = '0';
    frac = '0'.repeat(-d.exp - digits.length) + digits;
  } else {
    int = digits.slice(0, digits.length + d.exp);
    frac = digits.slice(digits.length + d.exp);
  }
  const grouped = int.length > 3 ? int.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : int;
  return frac ? `${grouped},${frac}` : grouped;
}

/* ------------------------------------------------------------------ */
/* conversion et tableau                                               */
/* ------------------------------------------------------------------ */

export function convert(d: Dec, from: UnitDef, to: UnitDef): Dec {
  return shift(d, from.pos - to.pos);
}

/** 1 m = 100 cm : le facteur (puissance de 10) et le sens */
export function factor(from: UnitDef, to: UnitDef): { k: number; text: string } {
  const k = from.pos - to.pos;
  return { k, text: decText({ m: 1n, exp: Math.abs(k) }) };
}

export interface Column {
  /** exposant de 10 de la colonne (unité de base) */
  exp: number;
  /** unité dont cette colonne fait partie (null : colonne ajoutée hors des unités) */
  group: UnitDef | null;
  /** unité dont c'est le chiffre des unités (dernière sous-colonne) */
  unit: UnitDef | null;
  /** autre nom posé sur cette colonne (L, ha…) */
  alias: UnitDef | null;
}

/** plus de colonnes que cela : le nombre ne se pose pas dans le tableau */
export const MAX_COLUMNS = 22;

/** exposants occupés par un nombre posé de `from` vers `to` */
export function span(d: Dec, from: UnitDef, to: UnitDef): { hi: number; lo: number } {
  const lowExp = from.pos + d.exp;
  const highExp = lowExp + d.m.toString().length - 1;
  return { hi: Math.max(highExp, from.pos, to.pos), lo: Math.min(lowExp, from.pos, to.pos) };
}

/**
 * Colonnes du tableau, de gauche à droite ; s'il le faut, on en ajoute (sans
 * nom) à gauche ou à droite pour que le nombre y tienne, comme on rajoute
 * une case à gauche des km.
 */
export function columns(kind: KindDef, need?: { hi: number; lo: number }): Column[] {
  const main = kind.units.filter((u) => !u.alias);
  const hiU = Math.max(...main.map((u) => u.pos)) + kind.digits - 1;
  const loU = Math.min(...main.map((u) => u.pos));
  const hi = Math.max(hiU, need?.hi ?? hiU);
  const lo = Math.min(loU, need?.lo ?? loU);
  const out: Column[] = [];
  for (let e = hi; e >= lo; e--)
    out.push({
      exp: e,
      group: main.find((u) => e >= u.pos && e <= u.pos + kind.digits - 1) ?? null,
      unit: main.find((u) => u.pos === e) ?? null,
      alias: kind.units.find((u) => u.alias && u.pos === e) ?? null,
    });
  return out;
}

export interface Cell {
  exp: number;
  /** chiffre affiché, ou null (case vide) */
  digit: number | null;
  /** zéro ajouté pour placer la virgule d'arrivée */
  added: boolean;
}

/**
 * Le nombre posé dans le tableau : le chiffre des unités de `from` dans sa
 * colonne ; les zéros nécessaires pour lire le résultat en `to` (entre le
 * nombre et la virgule d'arrivée) sont marqués « ajoutés ». null si le
 * nombre sort des colonnes données.
 */
export function placeDigits(
  d: Dec,
  from: UnitDef,
  to: UnitDef,
  cols: readonly Column[],
): Cell[] | null {
  const hiCol = cols[0]!.exp;
  const loCol = cols[cols.length - 1]!.exp;
  if (d.m === 0n)
    return cols.map((c) => ({
      exp: c.exp,
      digit: c.exp === from.pos || c.exp === to.pos ? 0 : null,
      added: c.exp === to.pos && to.pos !== from.pos,
    }));
  const s = d.m.toString();
  const lowExp = from.pos + d.exp;
  const highExp = lowExp + s.length - 1;
  const digitAt = new Map<number, number>();
  for (let i = 0; i < s.length; i++) digitAt.set(highExp - i, Number(s[i]));
  const { hi, lo } = span(d, from, to);
  if (hi > hiCol || lo < loCol) return null;
  return cols.map((c) => {
    const v = digitAt.get(c.exp);
    if (v !== undefined) return { exp: c.exp, digit: v, added: false };
    // zéros entre le chiffre des unités et le nombre (au départ : 0,5 → « 0 » ; à l'arrivée : 3 m → 300 cm)
    const insideFrom = c.exp <= Math.max(highExp, from.pos) && c.exp >= Math.min(lowExp, from.pos);
    const insideTo = c.exp <= Math.max(highExp, to.pos) && c.exp >= Math.min(lowExp, to.pos);
    if (insideFrom) return { exp: c.exp, digit: 0, added: false };
    if (insideTo) return { exp: c.exp, digit: 0, added: true };
    return { exp: c.exp, digit: null, added: false };
  });
}

/** « kilomètres », « mètres carrés », « litres » : pluriel à partir de 2 */
export const unitName = (u: UnitDef, d: Dec) => {
  const two = d.m * 10n ** BigInt(Math.max(0, d.exp)) >= 2n * 10n ** BigInt(Math.max(0, -d.exp));
  return two
    ? u.name
        .split(' ')
        .map((w) => `${w}s`)
        .join(' ')
    : u.name;
};

/** la phrase de la règle : « 1 m = 100 cm : on multiplie par 100… » */
export function ruleText(kind: KindDef, from: UnitDef, to: UnitDef): string {
  const { k, text } = factor(from, to);
  if (k === 0) {
    if (from.id === to.id) return 'Même unité : rien ne change.';
    return `1 ${from.label} = 1 ${to.label} : la même quantité, deux noms.`;
  }
  const ranks = Math.abs(k);
  const rang = `${ranks} rang${ranks > 1 ? 's' : ''}`;
  const per =
    kind.digits > 1 && !from.alias && !to.alias
      ? ` (${kind.digits} chiffres par unité dans le tableau des ${kind.title.toLowerCase()})`
      : '';
  return k > 0
    ? `1 ${from.label} = ${text} ${to.label} : on multiplie par ${text}, la virgule avance de ${rang} vers la droite${per}.`
    : `1 ${to.label} = ${text} ${from.label} : on divise par ${text}, la virgule recule de ${rang} vers la gauche${per}.`;
}

/* ------------------------------------------------------------------ */
/* vitesses (grandeur quotient)                                        */
/* ------------------------------------------------------------------ */

export type Speed = 'kmh' | 'ms';

/** v km/h en m/s (ou l'inverse), exact : fraction p/q réduite */
export function speedConvert(d: Dec, from: Speed): { p: bigint; q: bigint } {
  // valeur = m × 10^exp ; km/h → m/s : × 1000 / 3600 = × 5 / 18
  let p = d.m * (d.exp >= 0 ? 10n ** BigInt(d.exp) : 1n);
  let q = d.exp < 0 ? 10n ** BigInt(-d.exp) : 1n;
  if (from === 'kmh') {
    p *= 5n;
    q *= 18n;
  } else {
    p *= 18n;
    q *= 5n;
  }
  const g = gcd(p, q);
  return { p: p / g, q: q / g };
}

function gcd(a: bigint, b: bigint): bigint {
  while (b) [a, b] = [b, a % b];
  return a || 1n;
}

/** p/q : décimal exact s'il existe (6 décimales au plus), sinon null */
export function fracToDec(p: bigint, q: bigint): Dec | null {
  for (let k = 0; k <= 6; k++) {
    const scaled = p * 10n ** BigInt(k);
    if (scaled % q === 0n) return normalize({ m: scaled / q, exp: -k });
  }
  return null;
}

/** p/q arrondi au dixième, en texte */
export function approxText(p: bigint, q: bigint, digits = 1): string {
  const v = Number(p) / Number(q);
  return v.toFixed(digits).replace('.', ',');
}
